import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {
  UuidAuthResponse,
  HEADER_KEY_AUTHORIZATION,
  HEADER_KEY_CONTENT_TYPE,
  HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
} from '../../models';
import { AbstractStateService } from '../state/abstract-state.service';
import { ServerCallerService } from '../server-caller/server-caller.service';

@Injectable({ providedIn: 'root' })
export class ExchangeTokenServerCallerService {
  constructor(
    private stateService: AbstractStateService,
    private serverCaller: ServerCallerService
  ) {}

  public async get(boId: number, path: string): Promise<any> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    const token = this.stateService.getUuidAuthResponseForBoId(boId)?.token;

    return this.serverCaller.get(path, this.generateCustomerTokenHeader(token));
  }

  public async post(boId: number, path: string, data: any): Promise<any> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    const token = this.stateService.getUuidAuthResponseForBoId(boId)?.token;

    return this.serverCaller.post(
      path,
      data,
      this.generateCustomerTokenHeader(token)
    );
  }

  public async put(boId: number, path: string, data: any): Promise<any> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    const token = this.stateService.getUuidAuthResponseForBoId(boId)?.token;

    return this.serverCaller.put(
      path,
      data,
      this.generateCustomerTokenHeader(token)
    );
  }

  public async patch(boId: number, path: string, data: any): Promise<any> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    const token = this.stateService.getUuidAuthResponseForBoId(boId)?.token;

    return this.serverCaller.patch(
      path,
      data,
      this.generateCustomerTokenHeader(token)
    );
  }

  public async delete(boId: number, path: string, data?: any): Promise<any> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    const token = this.stateService.getUuidAuthResponseForBoId(boId)?.token;

    return this.serverCaller.delete(
      path,
      data,
      this.generateCustomerTokenHeader(token)
    );
  }

  public async getUuidAuthResponse(boId: number): Promise<UuidAuthResponse> {
    if (!this.stateService.hasUuidAuthResponseForBoId(boId)) {
      await this.exchangeUserTokenToCustomerToken(boId);
    }

    return this.stateService.getUuidAuthResponseForBoId(boId);
  }

  public async exchangeUserTokenToCustomerToken(
    boId: number
  ): Promise<UuidAuthResponse> {
    const url = `/v1/business-object/${boId}/user-customer-token`;
    const response = await this.serverCaller.get(
      url,
      this.generateUserTokenHeader()
    );

    const uuidAuthResponse = response?.body as UuidAuthResponse;

    if (uuidAuthResponse?.token) {
      this.stateService.setUuidAuthResponseForBoId(boId, uuidAuthResponse);
    }

    return uuidAuthResponse;
  }

  private generateCustomerTokenHeader(customerToken: string): HttpHeaders {
    if (customerToken) {
      return new HttpHeaders({
        [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
        [HEADER_KEY_AUTHORIZATION]: `Bearer${customerToken}`,
      });
    } else {
      return new HttpHeaders({
        [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
      });
    }
  }

  private generateUserTokenHeader(): HttpHeaders {
    const userToken = this.stateService.getToken();

    if (userToken) {
      return new HttpHeaders({
        [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
        [HEADER_KEY_AUTHORIZATION]: userToken,
      });
    } else {
      return new HttpHeaders({
        [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
      });
    }
  }
}
